import React, { useState, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import { getAdminMasterReport, getAdminSchools } from "@/actions/reportsAdmin";
import {
  Download, LayoutDashboard, School, Users, TrendingUp,
  Search, DollarSign, CheckCircle, Info, X, Filter, ChevronDown,
} from "lucide-react";

interface SchoolOption {
  slate_id: number;
  slate_name: string;
  cityName: string;
}

interface MasterReportData {
  slate_id: number;
  slate_name: string;
  views: number;
  areaName: string;
  cityName: string;
  total_leads: number;
  fb_leads: number;
  google_leads: number;
  auto_assigned_leads: number;
  organic_leads: number;
  admin_quality: number;
  client_quality: number;
  qualified_leads: number;
  fb_qualified_leads: number;
  auto_assigned_quality: number;
  meta_quality: number;
  fb_new_leads: number;
  total_new_leads: number;
  fb_campaigns: number;
  fb_campaign_names: string[];
  fb_form_ids: string[];
  fb_impressions: number;
  fb_reach: number;
  fb_results: number;
  fb_spend: number;
  fb_clicks: number;
  fb_cpr: number;
  fb_ctr: number;
  google_spend: number;
  google_clicks: number;
  google_impressions: number;
  google_ctr: number;
  google_cpr: number;
  google_campaign_names: string[];
  blog_count: number;
  blog_listing_views: number;
  blog_views: number;
  blog_titles: { title: string; slug: string }[];
  blog_listing_titles: { title: string; slug: string }[];
  total_spends: number;
  cpl: number;
  cpql: number;
  fb_quality_pct: number;
  total_quality_pct: number;
  auto_assigned_quality_pct: number;
  [key: string]: string | number | string[] | { title: string; slug: string }[];
}

interface Totals {
  schools: number;
  views: number;
  total_leads: number;
  fb_leads: number;
  google_leads: number;
  auto_assigned_leads: number;
  organic_leads: number;
  admin_quality: number;
  client_quality: number;
  qualified_leads: number;
  fb_qualified_leads: number;
  auto_assigned_quality: number;
  meta_quality: number;
  fb_new_leads: number;
  total_new_leads: number;
  fb_campaigns: number;
  fb_impressions: number;
  fb_reach: number;
  fb_results: number;
  fb_spend: number;
  fb_clicks: number;
  fb_cpr: number;
  fb_ctr: number;
  google_spend: number;
  google_clicks: number;
  google_impressions: number;
  google_ctr: number;
  google_cpr: number;
  total_spends: number;
  cpl: number;
  cpql: number;
  fb_quality_pct: number;
  total_quality_pct: number;
  auto_assigned_quality_pct: number;
  blog_count: number;
  blog_listing_views: number;
  blog_views: number;
}

const fmt = (n: number) => n?.toLocaleString() || "0";

const BlogInfoPopover: React.FC<{ blogs: { title: string; slug: string }[]; label: string }> = ({ blogs, label }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node) &&
          btnRef.current && !btnRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
    }
    setOpen(!open);
  };

  if (!blogs?.length) return null;
  return (
    <>
      <button ref={btnRef} onClick={handleToggle} className="ml-1 text-cyan-400 hover:text-cyan-600 transition-colors">
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div ref={popRef} className="fixed w-96 bg-white rounded-lg shadow-xl border border-gray-200 p-3"
          style={{ top: pos.top, left: pos.left, transform: "translate(-50%, -100%)", zIndex: 9999 }}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">{label} ({blogs.length})</span>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
          </div>
          <ul className="space-y-1.5 max-h-48 overflow-y-auto">
            {blogs.map((b, i) => (
              <li key={i} className="text-xs leading-snug flex gap-1.5">
                <span className="text-cyan-500 mt-0.5 shrink-0">{i + 1}.</span>
                <a href={`https://yellowslate.com/blog/${b.slug}`} target="_blank" rel="noopener noreferrer"
                  className="text-cyan-700 hover:text-cyan-900 hover:underline">{b.title}</a>
              </li>
            ))}
          </ul>
          <div className="absolute w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45"
            style={{ bottom: "-6px", left: "50%", transform: "translateX(-50%) rotate(45deg)" }} />
        </div>
      )}
    </>
  );
};

const thBase = "px-3 py-3 text-left text-xs font-medium uppercase whitespace-nowrap sticky top-0 z-30";
const thGray = `${thBase} text-gray-500 bg-gray-50`;
const thCyan = `${thBase} text-cyan-600 bg-cyan-50`;
const thRed = `${thBase} text-red-600 bg-red-50`;
const thGreen = `${thBase} text-green-600 bg-green-50`;
const thOrange = `${thBase} text-orange-600 bg-orange-50`;
const thBlue = `${thBase} text-blue-600 bg-blue-50`;
const thPurple = `${thBase} text-purple-600 bg-purple-50`;

const MasterReport: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(moment().startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [reportData, setReportData] = useState<MasterReportData[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [includeDuplicates, setIncludeDuplicates] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // School multi-select
  const [allSchools, setAllSchools] = useState<SchoolOption[]>([]);
  const [selectedSchools, setSelectedSchools] = useState<SchoolOption[]>([]);
  const [schoolSearch, setSchoolSearch] = useState<string>("");
  const [showSchoolDropdown, setShowSchoolDropdown] = useState<boolean>(false);
  const schoolPickerRef = useRef<HTMLDivElement>(null);

  const filteredSchoolOptions = useMemo(() => {
    const excluded = new Set(selectedSchools.map(s => s.slate_id));
    const pool = allSchools.filter(s => !excluded.has(s.slate_id));
    if (!schoolSearch.trim()) return pool.slice(0, 60);
    const term = schoolSearch.toLowerCase();
    return pool.filter(s =>
      s.slate_name.toLowerCase().includes(term) ||
      s.cityName?.toLowerCase().includes(term)
    ).slice(0, 60);
  }, [allSchools, schoolSearch, selectedSchools]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return reportData;
    const term = searchTerm.toLowerCase();
    return reportData.filter(s =>
      s.slate_name?.toLowerCase().includes(term) ||
      s.cityName?.toLowerCase().includes(term) ||
      s.areaName?.toLowerCase().includes(term)
    );
  }, [reportData, searchTerm]);

  // Load school list on mount (lightweight, no report data)
  useEffect(() => {
    getAdminSchools().then((res: any) => {
      if (res?.success && res.data?.length) {
        setAllSchools(res.data.map((s: any) => ({
          slate_id: s.slate_id,
          slate_name: s.slate_name,
          cityName: s.cityName || "",
        })));
      }
    }).catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (schoolPickerRef.current && !schoolPickerRef.current.contains(e.target as Node))
        setShowSchoolDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: Parameters<typeof getAdminMasterReport>[0] = {
        from_date: fromDate,
        to_date: toDate,
        includeDuplicates: includeDuplicates ? 1 : 0,
      };
      if (selectedSchools.length > 0) {
        params.slate_ids = selectedSchools.map(s => s.slate_id);
      }

      const response = await getAdminMasterReport(params);
      if (response.success) {
        const res = response as unknown as { data: MasterReportData[]; totals: Totals };
        const data = res.data || [];
        setReportData(data);
        setTotals(res.totals);
        setHasLoaded(true);
      }
    } catch (err) {
      console.error("Error fetching master report:", err);
    } finally {
      setLoading(false);
    }
  };

  const addSchool = (school: SchoolOption) => {
    setSelectedSchools(prev => [...prev, school]);
    setSchoolSearch("");
    setShowSchoolDropdown(false);
  };

  const removeSchool = (slateId: number) => {
    setSelectedSchools(prev => prev.filter(s => s.slate_id !== slateId));
  };

  const clearAllSchools = () => {
    setSelectedSchools([]);
    setSchoolSearch("");
  };

  const exportToCSV = () => {
    const headers = [
      "School ID", "School Name", "City", "Area", "Blogs", "Blog Listing Views", "Blog Views",
      "Total Spends", "FB Spends", "FB Reach", "Google Spends", "Google Clicks", "Google Impressions", "Google CTR%", "Google CPR",
      "Total Leads", "FB Leads", "Leads on Meta",
      "Google Leads", "Auto-assigned Leads", "Organic Leads", "CPL", "Qualified Leads", "Admin Quality",
      "Client Quality", "Meta Quality", "Auto-assigned Quality", "CPQL", "FB New Leads", "Total New Leads",
      "FB Quality %", "Total Quality %", "Auto-assigned Quality %",
    ];
    const rows = [headers.join(",")];
    filteredData.forEach(s => {
      rows.push([
        s.slate_id, `"${s.slate_name}"`, `"${s.cityName || ""}"`, `"${s.areaName || ""}"`,
        s.blog_count || 0, s.blog_listing_views || 0, s.blog_views || 0,
        s.total_spends?.toFixed(2) || "0.00", s.fb_spend?.toFixed(2) || "0.00", s.fb_reach || 0,
        s.google_spend?.toFixed(2) || "0.00", s.google_clicks || 0, s.google_impressions || 0,
        s.google_ctr?.toFixed(2) || "0.00", s.google_cpr?.toFixed(2) || "0.00",
        s.total_leads, s.fb_leads, s.fb_results,
        s.google_leads, s.auto_assigned_leads || 0, s.organic_leads,
        s.cpl?.toFixed(2) || "0.00", s.qualified_leads,
        s.admin_quality, s.client_quality, s.meta_quality, s.auto_assigned_quality || 0,
        s.cpql?.toFixed(2) || "0.00", s.fb_new_leads || 0, s.total_new_leads || 0,
        s.fb_quality_pct?.toFixed(2) || "0.00", s.total_quality_pct?.toFixed(2) || "0.00",
        s.auto_assigned_quality_pct?.toFixed(2) || "0.00",
      ].join(","));
    });
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `master-report-${fromDate}_${toDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="h-full flex flex-col">

      {/* ── Page header ── */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-brandColor p-2 rounded-lg">
              <LayoutDashboard className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Master Report</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Combined School Summary &amp; FB Ads Performance</p>
            </div>
          </div>
          {hasLoaded && (
            <button onClick={exportToCSV} className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor text-white text-sm font-medium rounded-lg hover:bg-brandColorDark">
              <Download className="h-4 w-4" />Export CSV
            </button>
          )}
        </div>

        {/* ── Filter bar ── */}
        <div className="px-4 pb-4 flex flex-wrap items-end gap-4 border-t border-gray-100 dark:border-gray-800 pt-3">

          {/* Date range */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500 uppercase">Date Range</label>
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5">
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                className="border-none bg-transparent focus:ring-0 text-sm text-gray-700 dark:text-gray-300 w-32" />
              <span className="text-gray-400 text-sm">to</span>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                className="border-none bg-transparent focus:ring-0 text-sm text-gray-700 dark:text-gray-300 w-32" />
            </div>
          </div>

          {/* Include duplicates */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500 uppercase">Options</label>
            <label className="flex items-center gap-2 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 cursor-pointer">
              <input type="checkbox" checked={includeDuplicates} onChange={e => setIncludeDuplicates(e.target.checked)} className="rounded" />
              Include Duplicates
            </label>
          </div>

          {/* School multi-select */}
          <div className="flex flex-col gap-1 flex-1 min-w-[260px] max-w-[500px]">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-500 uppercase">Schools</label>
              {selectedSchools.length > 0 && (
                <button onClick={clearAllSchools} className="text-xs text-brandColor hover:underline flex items-center gap-0.5">
                  <X className="h-3 w-3" />All Schools
                </button>
              )}
            </div>

            {/* Selected chips */}
            {selectedSchools.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {selectedSchools.map(s => (
                  <span key={s.slate_id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-brandColor/10 text-brandColor text-xs rounded-full border border-brandColor/20">
                    {s.slate_name}
                    <button onClick={() => removeSchool(s.slate_id)} className="hover:text-red-500 transition-colors">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Search input */}
            <div ref={schoolPickerRef} className="relative">
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                <School className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <input
                  type="text"
                  placeholder={selectedSchools.length === 0 ? "All client schools — type to filter" : "Add another school…"}
                  value={schoolSearch}
                  onChange={e => { setSchoolSearch(e.target.value); setShowSchoolDropdown(true); }}
                  onFocus={() => setShowSchoolDropdown(true)}
                  className="border-none bg-transparent focus:ring-0 text-sm text-gray-700 flex-1 min-w-0"
                />
                <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              </div>

              {showSchoolDropdown && (
                <div className="absolute top-full mt-1 left-0 z-50 w-full min-w-[300px] bg-white rounded-lg shadow-xl border border-gray-200 max-h-56 overflow-y-auto">
                  {/* "All Schools" reset option */}
                  {selectedSchools.length > 0 && (
                    <button onClick={clearAllSchools}
                      className="w-full text-left px-3 py-2 text-sm font-medium text-brandColor hover:bg-brandColor/5 border-b border-gray-100 flex items-center gap-2">
                      <X className="h-3.5 w-3.5" />Show all client schools
                    </button>
                  )}
                  {filteredSchoolOptions.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-gray-400 text-center">
                      {allSchools.length === 0 ? "Loading schools…" : "No schools found"}
                    </div>
                  ) : (
                    filteredSchoolOptions.map(s => (
                      <button key={s.slate_id} onClick={() => addSchool(s)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex flex-col border-b border-gray-50 last:border-0">
                        <span className="font-medium text-gray-800">{s.slate_name}</span>
                        {s.cityName && <span className="text-xs text-gray-400">{s.cityName} · ID: {s.slate_id}</span>}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Apply button */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500 uppercase opacity-0 select-none">Apply</label>
            <button onClick={fetchData} disabled={loading}
              className="px-5 py-2 bg-brandColor text-white text-sm font-medium rounded-lg hover:bg-brandColorDark disabled:opacity-60 flex items-center gap-2">
              <Filter className="h-4 w-4" />
              {loading ? "Loading…" : "Apply"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 flex flex-col overflow-hidden p-6 gap-4">

        {/* Empty state */}
        {!hasLoaded && !loading && (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-3">
            <LayoutDashboard className="h-12 w-12 opacity-30" />
            <p className="text-sm">Set your filters and click <strong className="text-gray-500">Apply</strong> to load the report.</p>
          </div>
        )}

        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
          </div>
        )}

        {hasLoaded && !loading && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 shrink-0">
              {[
                { label: "Schools", value: fmt(totals?.schools || 0), color: "border-brandColor", Icon: School, iconColor: "text-brandColor" },
                { label: "Total Spends", value: fmt(Math.round(totals?.total_spends || 0)), color: "border-red-500", Icon: DollarSign, iconColor: "text-red-500" },
                { label: "Total Leads", value: fmt(totals?.total_leads || 0), color: "border-green-500", Icon: Users, iconColor: "text-green-500" },
                { label: "Qualified Leads", value: fmt(totals?.qualified_leads || 0), color: "border-blue-500", Icon: CheckCircle, iconColor: "text-blue-500" },
                { label: "CPL", value: totals?.cpl?.toFixed(2) || "0.00", color: "border-orange-500", Icon: TrendingUp, iconColor: "text-orange-500" },
                { label: "Total Quality %", value: `${totals?.total_quality_pct?.toFixed(1) || "0"}%`, color: "border-purple-500", Icon: CheckCircle, iconColor: "text-purple-500" },
              ].map(({ label, value, color, Icon, iconColor }) => (
                <div key={label} className={`background-container rounded-lg shadow px-2 py-1 border-l-4 ${color}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-xs gray-text">{label}</div>
                      <div className="text-lg font-bold">{value}</div>
                    </div>
                    <Icon className={`h-5 w-5 ${iconColor}`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Table */}
            <div className="background-container rounded-lg shadow flex-1 flex flex-col min-h-0">
              <div className="bg-gradient-to-r from-brandColor to-brand-hover px-6 py-4 rounded-t-lg flex justify-between items-center shrink-0">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <LayoutDashboard className="h-5 w-5" />
                  Master Report — School Performance
                  {selectedSchools.length > 0 && (
                    <span className="text-sm font-normal opacity-80">({selectedSchools.length} school{selectedSchools.length > 1 ? "s" : ""} selected)</span>
                  )}
                </h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input type="text" placeholder="Search school, city, area…" value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 text-sm rounded-lg border-0 focus:ring-2 focus:ring-white/50 w-64" />
                </div>
              </div>

              <div className="flex-1 overflow-x-auto overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className={thGray}>ID</th>
                      <th className={`${thGray} sticky left-0 z-40 min-w-[200px] max-w-[200px]`}>School</th>
                      <th className={thGray}>City</th>
                      <th className={thGray}>Area</th>
                      <th className={thCyan}>Blogs</th>
                      <th className={thCyan}>Blog Listing Views</th>
                      <th className={thCyan}>Blog Views</th>
                      <th className={thRed}>Total Spends</th>
                      <th className={thRed}>FB Spends</th>
                      <th className={thRed}>FB Reach</th>
                      <th className={thBlue}>Google Spends</th>
                      <th className={thBlue}>G Clicks</th>
                      <th className={thBlue}>G Impressions</th>
                      <th className={thBlue}>G CTR%</th>
                      <th className={thBlue}>G CPR</th>
                      <th className={thGreen}>Total Leads</th>
                      <th className={thGreen}>FB Leads</th>
                      <th className={thGreen}>Leads on Meta</th>
                      <th className={thGreen}>Google Leads</th>
                      <th className={thGreen}>Auto-assigned Leads</th>
                      <th className={thGreen}>Organic Leads</th>
                      <th className={thOrange}>CPL</th>
                      <th className={thBlue}>Qualified Leads</th>
                      <th className={thBlue}>Admin Quality</th>
                      <th className={thBlue}>Client Quality</th>
                      <th className={thBlue}>Meta Quality</th>
                      <th className={thBlue}>Auto-assigned Quality</th>
                      <th className={thPurple}>CPQL</th>
                      <th className={thPurple}>FB New Leads</th>
                      <th className={thPurple}>Total New Leads</th>
                      <th className={thPurple}>FB Quality %</th>
                      <th className={thPurple}>Total Quality %</th>
                      <th className={thPurple}>Auto-assigned Quality %</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map(s => (
                      <tr key={s.slate_id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">{s.slate_id}</td>
                        <td className="px-3 py-3 text-sm font-medium text-gray-900 sticky left-0 z-10 bg-white min-w-[200px] max-w-[200px] truncate" title={s.slate_name}>{s.slate_name}</td>
                        <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">{s.cityName || "-"}</td>
                        <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">{s.areaName || "-"}</td>
                        <td className="px-3 py-3 text-sm text-cyan-600 whitespace-nowrap bg-cyan-50/30">{s.blog_count || 0}</td>
                        <td className="px-3 py-3 text-sm text-cyan-600 whitespace-nowrap bg-cyan-50/30">
                          <span className="inline-flex items-center">{fmt(s.blog_listing_views || 0)}<BlogInfoPopover blogs={s.blog_listing_titles} label="Listing Blogs" /></span>
                        </td>
                        <td className="px-3 py-3 text-sm text-cyan-600 whitespace-nowrap bg-cyan-50/30">
                          <span className="inline-flex items-center">{fmt(s.blog_views || 0)}<BlogInfoPopover blogs={s.blog_titles} label="School Blogs" /></span>
                        </td>
                        <td className="px-3 py-3 text-sm text-red-600 whitespace-nowrap bg-red-50/30">{s.total_spends?.toFixed(2) || "0.00"}</td>
                        <td className="px-3 py-3 text-sm text-red-600 whitespace-nowrap bg-red-50/30">{s.fb_spend?.toFixed(2) || "0.00"}</td>
                        <td className="px-3 py-3 text-sm text-red-600 whitespace-nowrap bg-red-50/30">{fmt(s.fb_reach || 0)}</td>
                        <td className="px-3 py-3 text-sm text-blue-600 whitespace-nowrap bg-blue-50/30">{s.google_spend?.toFixed(2) || "0.00"}</td>
                        <td className="px-3 py-3 text-sm text-blue-600 whitespace-nowrap bg-blue-50/30">{fmt(s.google_clicks || 0)}</td>
                        <td className="px-3 py-3 text-sm text-blue-600 whitespace-nowrap bg-blue-50/30">{fmt(s.google_impressions || 0)}</td>
                        <td className="px-3 py-3 text-sm text-blue-600 whitespace-nowrap bg-blue-50/30">{s.google_ctr?.toFixed(2) || "0.00"}%</td>
                        <td className="px-3 py-3 text-sm text-blue-600 whitespace-nowrap bg-blue-50/30">{s.google_cpr?.toFixed(2) || "0.00"}</td>
                        <td className="px-3 py-3 text-sm font-bold text-green-700 whitespace-nowrap bg-green-50/30">{fmt(s.total_leads)}</td>
                        <td className="px-3 py-3 text-sm text-green-600 whitespace-nowrap bg-green-50/30">{fmt(s.fb_leads)}</td>
                        <td className="px-3 py-3 text-sm text-green-600 whitespace-nowrap bg-green-50/30">{fmt(s.fb_results)}</td>
                        <td className="px-3 py-3 text-sm text-green-600 whitespace-nowrap bg-green-50/30">{fmt(s.google_leads)}</td>
                        <td className="px-3 py-3 text-sm text-green-600 whitespace-nowrap bg-green-50/30">{fmt(s.auto_assigned_leads || 0)}</td>
                        <td className="px-3 py-3 text-sm text-green-600 whitespace-nowrap bg-green-50/30">{fmt(s.organic_leads)}</td>
                        <td className="px-3 py-3 text-sm text-orange-600 whitespace-nowrap bg-orange-50/30">{s.cpl?.toFixed(2) || "0.00"}</td>
                        <td className="px-3 py-3 text-sm font-bold text-blue-700 whitespace-nowrap bg-blue-50/30">{fmt(s.qualified_leads)}</td>
                        <td className="px-3 py-3 text-sm text-blue-600 whitespace-nowrap bg-blue-50/30">{fmt(s.admin_quality)}</td>
                        <td className="px-3 py-3 text-sm text-blue-600 whitespace-nowrap bg-blue-50/30">{fmt(s.client_quality)}</td>
                        <td className="px-3 py-3 text-sm text-blue-600 whitespace-nowrap bg-blue-50/30">{fmt(s.meta_quality)}</td>
                        <td className="px-3 py-3 text-sm text-blue-600 whitespace-nowrap bg-blue-50/30">{fmt(s.auto_assigned_quality || 0)}</td>
                        <td className="px-3 py-3 text-sm text-purple-600 whitespace-nowrap bg-purple-50/30">{s.cpql?.toFixed(2) || "0.00"}</td>
                        <td className="px-3 py-3 text-sm text-purple-600 whitespace-nowrap bg-purple-50/30">{fmt(s.fb_new_leads || 0)}</td>
                        <td className="px-3 py-3 text-sm text-purple-600 whitespace-nowrap bg-purple-50/30">{fmt(s.total_new_leads || 0)}</td>
                        <td className="px-3 py-3 text-sm text-purple-600 whitespace-nowrap bg-purple-50/30">{s.fb_quality_pct?.toFixed(2) || "0.00"}%</td>
                        <td className="px-3 py-3 text-sm text-purple-600 whitespace-nowrap bg-purple-50/30">{s.total_quality_pct?.toFixed(2) || "0.00"}%</td>
                        <td className="px-3 py-3 text-sm text-purple-600 whitespace-nowrap bg-purple-50/30">{s.auto_assigned_quality_pct?.toFixed(2) || "0.00"}%</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="sticky bottom-0 z-20">
                    <tr className="bg-gray-100 font-bold">
                      <td className="px-3 py-3 text-sm text-gray-900 bg-gray-100 whitespace-nowrap">-</td>
                      <td className="px-3 py-3 text-sm text-gray-900 bg-gray-100 sticky left-0 z-20 min-w-[200px] max-w-[200px]">Total ({filteredData.length})</td>
                      <td colSpan={2} className="px-3 py-3 text-sm text-gray-900 bg-gray-100 whitespace-nowrap">-</td>
                      <td className="px-3 py-3 text-sm text-cyan-700 bg-cyan-100 whitespace-nowrap">{fmt(totals?.blog_count || 0)}</td>
                      <td className="px-3 py-3 text-sm text-cyan-700 bg-cyan-100 whitespace-nowrap">{fmt(totals?.blog_listing_views || 0)}</td>
                      <td className="px-3 py-3 text-sm text-cyan-700 bg-cyan-100 whitespace-nowrap">{fmt(totals?.blog_views || 0)}</td>
                      <td className="px-3 py-3 text-sm text-red-700 bg-red-100 whitespace-nowrap">{totals?.total_spends?.toFixed(2) || "0.00"}</td>
                      <td className="px-3 py-3 text-sm text-red-700 bg-red-100 whitespace-nowrap">{totals?.fb_spend?.toFixed(2) || "0.00"}</td>
                      <td className="px-3 py-3 text-sm text-red-700 bg-red-100 whitespace-nowrap">{fmt(totals?.fb_reach || 0)}</td>
                      <td className="px-3 py-3 text-sm text-blue-700 bg-blue-100 whitespace-nowrap">{totals?.google_spend?.toFixed(2) || "0.00"}</td>
                      <td className="px-3 py-3 text-sm text-blue-700 bg-blue-100 whitespace-nowrap">{fmt(totals?.google_clicks || 0)}</td>
                      <td className="px-3 py-3 text-sm text-blue-700 bg-blue-100 whitespace-nowrap">{fmt(totals?.google_impressions || 0)}</td>
                      <td className="px-3 py-3 text-sm text-blue-700 bg-blue-100 whitespace-nowrap">{totals?.google_ctr?.toFixed(2) || "0.00"}%</td>
                      <td className="px-3 py-3 text-sm text-blue-700 bg-blue-100 whitespace-nowrap">{totals?.google_cpr?.toFixed(2) || "0.00"}</td>
                      <td className="px-3 py-3 text-sm text-green-700 bg-green-100 whitespace-nowrap">{fmt(totals?.total_leads || 0)}</td>
                      <td className="px-3 py-3 text-sm text-green-700 bg-green-100 whitespace-nowrap">{fmt(totals?.fb_leads || 0)}</td>
                      <td className="px-3 py-3 text-sm text-green-700 bg-green-100 whitespace-nowrap">{fmt(totals?.fb_results || 0)}</td>
                      <td className="px-3 py-3 text-sm text-green-700 bg-green-100 whitespace-nowrap">{fmt(totals?.google_leads || 0)}</td>
                      <td className="px-3 py-3 text-sm text-green-700 bg-green-100 whitespace-nowrap">{fmt(totals?.auto_assigned_leads || 0)}</td>
                      <td className="px-3 py-3 text-sm text-green-700 bg-green-100 whitespace-nowrap">{fmt(totals?.organic_leads || 0)}</td>
                      <td className="px-3 py-3 text-sm text-orange-700 bg-orange-100 whitespace-nowrap">{totals?.cpl?.toFixed(2) || "0.00"}</td>
                      <td className="px-3 py-3 text-sm text-blue-700 bg-blue-100 whitespace-nowrap">{fmt(totals?.qualified_leads || 0)}</td>
                      <td className="px-3 py-3 text-sm text-blue-700 bg-blue-100 whitespace-nowrap">{fmt(totals?.admin_quality || 0)}</td>
                      <td className="px-3 py-3 text-sm text-blue-700 bg-blue-100 whitespace-nowrap">{fmt(totals?.client_quality || 0)}</td>
                      <td className="px-3 py-3 text-sm text-blue-700 bg-blue-100 whitespace-nowrap">{fmt(totals?.meta_quality || 0)}</td>
                      <td className="px-3 py-3 text-sm text-blue-700 bg-blue-100 whitespace-nowrap">{fmt(totals?.auto_assigned_quality || 0)}</td>
                      <td className="px-3 py-3 text-sm text-purple-700 bg-purple-100 whitespace-nowrap">{totals?.cpql?.toFixed(2) || "0.00"}</td>
                      <td className="px-3 py-3 text-sm text-purple-700 bg-purple-100 whitespace-nowrap">{fmt(totals?.fb_new_leads || 0)}</td>
                      <td className="px-3 py-3 text-sm text-purple-700 bg-purple-100 whitespace-nowrap">{fmt(totals?.total_new_leads || 0)}</td>
                      <td className="px-3 py-3 text-sm text-purple-700 bg-purple-100 whitespace-nowrap">{totals?.fb_quality_pct?.toFixed(2) || "0.00"}%</td>
                      <td className="px-3 py-3 text-sm text-purple-700 bg-purple-100 whitespace-nowrap">{totals?.total_quality_pct?.toFixed(2) || "0.00"}%</td>
                      <td className="px-3 py-3 text-sm text-purple-700 bg-purple-100 whitespace-nowrap">{totals?.auto_assigned_quality_pct?.toFixed(2) || "0.00"}%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MasterReport;
